import React from 'react';
import { useTable, useSortBy, useGlobalFilter, usePagination, useExpanded } from 'react-table'
import { Input, Button } from 'reactstrap'

import './Table.scss'

const { useLocation, useHistory, useRouteMatch } = require('react-router-dom')

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

interface Props {
  data: any[];
  columns: any[];
  searchTitle?: string;
  paginationTitle?: string;

  expandableRow?: (item: any) => any;
  onRowClicked?: (item: any) => void;
  onChangePage?: (page: number, sort: any) => void;
}
const Table: React.FC<Props> = (props) => {
  const data = props.data
  const columns = React.useMemo(() => {
    return props.columns.map((item) => {
      if (item.cell) {
        item.Cell = (val: any) => {
          return item.cell(val.cell.row.original)
        }
      }
      return item
    })
  }, [props.columns])
 
  const history = useHistory()
  const match = useRouteMatch()
  const query = useQuery()
  const baseUrl = match.url
  const currentPage = Number(query.get('page') || 0)
  const currentSort = query.get('sort')
  const currentDesc = query.get('desc')
  const currentSize = Number(query.get('size') || 10)
  
  const {
    // Common
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,

    // Pagination
    canPreviousPage,
    canNextPage,
    pageOptions, 
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    // Global filter
    setGlobalFilter,

    state: {
      globalFilter,
      sortBy,
      pageIndex,
      pageSize,
      expanded
    },
  } = useTable({
    columns,
    data,
    initialState: {
      sortBy: currentSort ? [{ id: currentSort, desc: currentDesc }] : [],
      pageIndex: currentPage,
      pageSize: currentSize,
    },
  }, useGlobalFilter, useSortBy, useExpanded, usePagination)

  React.useEffect(() => {
    if (sortBy.length) {
      const sort = sortBy[0]
      if (sort.desc !== currentDesc || sort.id !== currentSort || currentPage !== pageIndex || currentSize !== pageSize)
        history.push(baseUrl + '?page=' + pageIndex + '&size=' + pageSize + '&sort=' + sort.id + '&desc=' + sort.desc)
    } else if (currentPage != pageIndex) {
      history.push(baseUrl + '?page=' + pageIndex + '&size=' + pageSize)
    }
  }, [
    currentPage, currentSort, currentSize, currentDesc,
    pageIndex, pageSize, sortBy
  ])

  React.useEffect(() => {
    if (globalFilter)
      gotoPage(0)
  }, [globalFilter]);

  const onRowClicked = (cell: any) => {
    if (!props.onRowClicked)
      return;
    if (cell.column.hasButton)
      return;
    props.onRowClicked(cell.row.original)
  }

  return (
     <>
      { props.searchTitle &&
        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
          <div style={{width: 250, marginBottom: 12}}>
            <Input
              key={'search-' + props.searchTitle}
              placeholder={props.searchTitle || 'Rechercher...'}
              value={globalFilter || ''} 
              onChange={(e) => setGlobalFilter(e.target.value)}
              />
          </div>
        </div>
      }

      <div className="table-wrapper">
        <div className="main-table" {...getTableProps()}>
          <div>
            {headerGroups.map(headerGroup => (
              <div className="table-row" {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <div
                    className="table-head"
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    style={{minWidth: column.width, maxWidth: column.width}}
                    >
                    {column.render('name')}
                    {column.isSorted &&
                      <span className="sort-indicator">{column.isSortedDesc ? '▼' : '▲'}</span>
                    }
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div {...getTableBodyProps()}>
            {page.length === 0 ? (
              <div className="table-empty">Aucun élement</div>
            ) : page.map(
              (row, i) => {
                prepareRow(row);
                return (
                  <React.Fragment key={row.id}>
                    <div className="table-row" {...row.getRowProps()} {...row.getToggleRowExpandedProps()}>
                      {row.cells.map(cell => {
                        return (
                          <div
                            className="table-cell"
                            onClick={() => onRowClicked(cell)}
                            {...cell.getCellProps()}
                            style={{minWidth: cell.column.width, maxWidth: cell.column.width}}
                            >
                            <div className="table-cell-content">
                            {cell.render('Cell')}</div>
                          </div>
                        )
                      })}
                    </div>
                    {row.isExpanded && props.expandableRow && (
                      <div>{props.expandableRow(row.original)}</div>
                    )}
                  </React.Fragment>
                )}
            )}
          </div>
        </div>
        <div className="pagination">
          <span>{props.paginationTitle || 'Elements'} par page:</span>
          <div className="page-size-wrapper">
            <select
              className="page-size-selector"
              value={pageSize}
              onChange={e => {
                setPageSize(Number(e.target.value))
              }}
            >
              {[10, 20, 30, 40, 50].map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
            <div className="floating-indicator">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M7 10l5 5 5-5z"></path><path d="M0 0h24v24H0z" fill="none"></path></svg>
            </div>
          </div>

          <span className="pagination-indicator">
            {pageIndex + 1} sur {pageOptions.length}
          </span>
          
          <button className="pagination-btn" onClick={() => gotoPage(0)} disabled={!canPreviousPage}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" role="presentation"><path d="M18.41 16.59L13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z"></path><path fill="none" d="M24 24H0V0h24v24z"></path></svg>
          </button>
          <button className="pagination-btn" onClick={() => previousPage()} disabled={!canPreviousPage}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" role="presentation"><path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"></path><path d="M0 0h24v24H0z" fill="none"></path></svg>
          </button>
          <button className="pagination-btn" onClick={() => nextPage()} disabled={!canNextPage}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" role="presentation"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"></path><path d="M0 0h24v24H0z" fill="none"></path></svg>
          </button>
          <button className="pagination-btn" onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" role="presentation"><path d="M5.59 7.41L10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z"></path><path fill="none" d="M0 0h24v24H0V0z"></path></svg>
          </button>
        </div>
      </div>
    </>
   )
}

export default Table